/* eslint-disable lodash/prefer-lodash-method */
import { useAuth0 } from '@auth0/auth0-react';
import { Role, Tier } from 'consts/auth';
import { differenceInDays, parseISO } from 'date-fns';
import { useEffect } from 'react';

const auth0Identifier = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:5000';
const disableChecks = import.meta.env.VITE_DISABLE_AUTH_CHECKS;

const tierClaim = `${auth0Identifier}/tier`;
const rolesClaim = `${auth0Identifier}/roles`;
const permsClaim = `${auth0Identifier}/permissions`;
const trialExpirationClaim = `${auth0Identifier}/trial_expiration`;
// HACK: PL-only is a frontend-only gate driven by an Auth0 custom claim.
// The backend does not enforce it — anyone with the JWT can still call APIs
// directly. Replace with proper backend + MCP enforcement.
const plOnlyClaim = `${auth0Identifier}/pl_only`;

function useValidUser () {
  const { user, logout, isLoading, isAuthenticated } = useAuth0();
  const hasTier = user?.[tierClaim];
  const hasRoles = user?.[rolesClaim];
  const hasPermissions = user?.[permsClaim];
  const hasValidToken = hasTier && hasRoles && hasPermissions;
  if (!isLoading && isAuthenticated && !hasValidToken) logout();
  return { user };
}

// A brand-new signup's FIRST token is minted before the Auth0 post-login
// action assigns their role, so its RBAC permissions array is empty — which
// 403s permission-gated endpoints until the next natural token refresh.
// One forced refresh (cacheMode: 'off') picks up the now-assigned role.
// Session-scoped guard so a user who genuinely has zero permissions can't
// cause a refresh loop.
export function useHealEmptyFirstTokenPermissions () {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const permissions = user?.[permsClaim];
  const needsHeal =
    isAuthenticated && Array.isArray(permissions) && permissions.length === 0;

  useEffect(() => {
    if (!needsHeal) return;
    const guardKey = 'auth0-perm-heal-attempted';
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, '1');
    getAccessTokenSilently({ cacheMode: 'off' }).catch(() => {
      // Best-effort: a failed refresh just leaves the first-token state,
      // which heals on the next natural token renewal anyway.
    });
  }, [needsHeal, getAccessTokenSilently]);
}

const INTERNAL_DOMAINS = ['@oilytics.co', '@hiloanalytics.com'];
const INTERNAL_EMAILS = ['nmaheshwari@wesleyan.edu'];

export function useIsInternalUser (): boolean {
  const { user } = useAuth0();
  const email = user?.email || '';
  return INTERNAL_EMAILS.includes(email) || INTERNAL_DOMAINS.some(d => email.endsWith(d));
}

export function useHasRole (roleName: string) {
  const { user } = useValidUser();
  const userRoles: string[] = user?.[rolesClaim] || [Role.FullAccess];
  const hasRole = userRoles.includes(roleName);
  return disableChecks ? true : hasRole;
}

export function useHasPermission (permissionName: string) {
  const { user } = useValidUser();
  const userPermissions: string[] = user?.[permsClaim] || [];
  const hasPermission = userPermissions.includes(permissionName);
  return disableChecks ? true : hasPermission;
}

// HACK: frontend-only check for the PL-only flag from Auth0 app_metadata.
// Returns true when the user should see *only* Petro-Logistics Shiptracking.
// Not a security boundary — UX gate only.
export function useIsPLOnly (): boolean {
  const { user } = useValidUser();
  const isPLOnly = Boolean(user?.[plOnlyClaim]);
  return disableChecks ? false : isPLOnly;
}

export function useHasTier (tierName: string) {
  const { user } = useValidUser();
  const userTier: string = user?.[tierClaim] || Tier.Paid;
  const hasTier = userTier.includes(tierName);
  return disableChecks ? true : hasTier;
}

// Free tier AND expired trial: both carry only BasicAccessRole, and every
// free-tier surface (e.g. TradingView attribution) must
// treat them the same. Gate on the role, not the tier claim, which still
// reads 'trial' after expiry. A missing roles claim reads as FullAccess
// (pre-freemium paid tokens), matching the backend's has_full_access.
export function useIsFreeTier () {
  return !useHasRole(Role.FullAccess);
}

export function useTrial (): { isActive: boolean, isExpired: boolean, daysLeft: number } | null {
  const { user } = useValidUser();
  const isTrialUser = useHasTier(Tier.Trial);
  const hasBasicAccess = useHasRole(Role.BasicAccess);
  const hasFullAccess = useHasRole(Role.FullAccess);
  const hasActiveTrial = isTrialUser && hasFullAccess;
  const hasTrialExpired = isTrialUser && hasBasicAccess;

  if (disableChecks || !isTrialUser) return null;

  const defaultTrial = {
    isActive: hasActiveTrial,
    isExpired: hasTrialExpired,
    daysLeft: 0,
  };

  const trialExpiresAt = user?.[trialExpirationClaim] || null;
  if (trialExpiresAt) {
    const trialExpirationDate = parseISO(trialExpiresAt);
    const now = new Date();
    const daysLeft = differenceInDays(trialExpirationDate, now);
    return {
      ...defaultTrial,
      daysLeft,
    };
  }

  return defaultTrial;
}
